
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface ProcessPaymentData {
  clientId: string;
  paymentAmount: number;
  paymentDate?: string;
  notes?: string;
  subscriptionId: string; // Now required - all payments must be linked to a subscription
}

interface ProcessPaymentResult {
  success: boolean;
  payment_id: string | null;
  subscription_payment_status: string | null;
  client_payment_status: string;
  payment_amount: number;
  cycle_reset: boolean;
  carried_over_sessions: number;
  is_first_cycle: boolean;
}

export const useProcessClientPayment = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: ProcessPaymentData): Promise<ProcessPaymentResult> => {
      console.log('Processing client payment for subscription:', data);

      // Use the updated process_client_payment function with required subscription_id parameter
      const { data: result, error } = await supabase.rpc(
        'process_client_payment',
        {
          p_client_id: data.clientId,
          p_payment_amount: data.paymentAmount,
          p_subscription_id: data.subscriptionId, // Now required parameter
          p_payment_date: data.paymentDate || new Date().toISOString().split('T')[0],
          p_notes: data.notes || null,
        }
      );

      if (error) {
        console.error('Error processing payment:', error);
        throw new Error(`Failed to process payment: ${error.message}`);
      }

      if (!result) {
        throw new Error('No result returned from payment processing');
      }

      console.log('Payment processed successfully with session-based logic:', result);
      
      const parsedResult = result as unknown as ProcessPaymentResult;
      
      if (typeof parsedResult !== 'object' || 
          !('success' in parsedResult) || 
          !('client_payment_status' in parsedResult)) {
        throw new Error('Invalid response structure from payment processing');
      }

      return parsedResult;
    },
    onSuccess: async (result, variables) => {
      console.log('Payment mutation succeeded with full result:', result);
      console.log('Payment variables:', variables);
      
      // Step 1: Invalidate all client-related queries immediately
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['clients'] }),
        queryClient.invalidateQueries({ queryKey: ['client', variables.clientId] }),
        queryClient.invalidateQueries({ queryKey: ['clientPayments', variables.clientId] }),
        queryClient.invalidateQueries({ queryKey: ['clientSubscriptions', variables.clientId] }),
        queryClient.invalidateQueries({ queryKey: ['unpaid-sessions', variables.clientId] }),
        queryClient.invalidateQueries({ queryKey: ['client-sessions', variables.clientId] }),
        // Add subscription payments invalidation for the specific subscription
        queryClient.invalidateQueries({ queryKey: ['subscriptionPayments', variables.clientId, variables.subscriptionId] }),
        queryClient.invalidateQueries({ queryKey: ['subscriptionPayments', variables.clientId] }),
      ]);
      
      // Step 2: Force immediate refetch of critical data with fresh data
      await Promise.all([
        queryClient.refetchQueries({ 
          queryKey: ['client', variables.clientId],
          type: 'active'
        }),
        queryClient.refetchQueries({ 
          queryKey: ['clients'],
          type: 'active'
        }),
        queryClient.refetchQueries({ 
          queryKey: ['clientPayments', variables.clientId],
          type: 'active'
        }),
        queryClient.refetchQueries({
          queryKey: ['unpaid-sessions', variables.clientId],
          type: 'active'
        }),
        queryClient.refetchQueries({
          queryKey: ['client-sessions', variables.clientId],
          type: 'active'
        }),
      ]);

      // Step 3: Update client data in cache immediately to reflect new payment status
      queryClient.setQueryData(['client', variables.clientId], (oldData: any) => {
        if (oldData) {
          return {
            ...oldData,
            paymentStatus: result.client_payment_status,
            lastPaymentAmount: result.payment_amount,
            lastPaymentDate: new Date().toISOString().split('T')[0]
          };
        }
        return oldData;
      });

      // Step 4: Update clients list cache to reflect new payment status and subscription data
      queryClient.setQueryData(['clients'], (oldData: any[]) => {
        if (oldData && variables.subscriptionId) {
          return oldData.map(client => 
            client.id === variables.clientId 
              ? {
                  ...client,
                  paymentStatus: result.client_payment_status,
                  lastPaymentAmount: result.payment_amount,
                  lastPaymentDate: new Date().toISOString().split('T')[0],
                  subscriptions: client.subscriptions?.map((sub: any) => 
                    sub.id === variables.subscriptionId
                      ? {
                          ...sub,
                          paymentStatus: result.subscription_payment_status || 'paid',
                          // If cycle was reset, update the cycle data
                          currentCycleSessionsUsed: result.cycle_reset ? 0 : sub.currentCycleSessionsUsed,
                          currentCycleStartDate: result.cycle_reset 
                            ? (variables.paymentDate || new Date().toISOString().split('T')[0])
                            : sub.currentCycleStartDate
                        }
                      : sub
                  ) || []
                }
              : client
          );
        }
        return oldData;
      });

      console.log('UI state updated successfully with new payment data');
    },
    onError: (error) => {
      console.error('Payment mutation failed:', error);
    }
  });
};
